// src/pages/trading-portal/MarketDataIngestion/FedLiquidityEnginePage.js
// Fed Net Liquidity correlation engine - analysis page

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Alert,
    Button,
    TextField,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Card,
    CardContent,
    Chip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Tabs,
    Tab,
    Tooltip,
    Autocomplete,
} from '@mui/material';
import {
    Water as WaterIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
    Calculate as CalculateIcon,
    Warning as WarningIcon,
    Assessment as AssessmentIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    MacroRegime,
    MacroRegimeLabels,
    MacroRegimeColors,
    DivergenceDirection,
    DivergenceDirectionLabels,
    DivergenceDirectionColors,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const formatBillions = (value) => {
    if (value == null) return '-';
    return `$${parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}B`;
};

const formatCorrelation = (value) => {
    if (value == null) return '-';
    return parseFloat(value).toFixed(4);
};

const formatPercent = (value) => {
    if (value == null) return '-';
    return `${parseFloat(value).toFixed(2)}%`;
};

const FedLiquidityEnginePage = () => {
    // Permission checks
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);

    // Tab state
    const [activeTab, setActiveTab] = useState(0);

    // Symbols for autocomplete
    const [symbols, setSymbols] = useState([]);
    const [loadingSymbols, setLoadingSymbols] = useState(true);

    // General state
    const [error, setError] = useState(null);

    // Dashboard state
    const [dashboardData, setDashboardData] = useState(null);
    const [loadingDashboard, setLoadingDashboard] = useState(true);

    // Calculate Correlation state
    const [calcSymbol, setCalcSymbol] = useState(null);
    const [calcWindow, setCalcWindow] = useState(90);
    const [calcRegime, setCalcRegime] = useState('');
    const [calculating, setCalculating] = useState(false);
    const [calcResult, setCalcResult] = useState(null);

    // Correlations table state
    const [correlations, setCorrelations] = useState([]);
    const [corrLoading, setCorrLoading] = useState(false);
    const [corrPage, setCorrPage] = useState(0);
    const [corrRowsPerPage, setCorrRowsPerPage] = useState(10);
    const [corrTotalCount, setCorrTotalCount] = useState(0);
    const [corrSymbolFilter, setCorrSymbolFilter] = useState(null);

    // Divergence Detector state
    const [divSymbol, setDivSymbol] = useState(null);
    const [detecting, setDetecting] = useState(false);
    const [divResult, setDivResult] = useState(null);

    // Predictive Model state
    const [predSymbol, setPredSymbol] = useState(null);
    const [loadingPredictive, setLoadingPredictive] = useState(false);
    const [predictiveResult, setPredictiveResult] = useState(null);

    useEffect(() => {
        const checkPermissions = async () => {
            const view = await hasPermission('tr-mdi-liquidity-engine', 'view');
            const add = await hasPermission('tr-mdi-liquidity-engine', 'add');
            setCanView(view);
            setCanAdd(add);
        };
        checkPermissions();
    }, []);

    // Fetch symbols for autocomplete
    const fetchSymbols = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/stock-trading/symbols?per_page=200');
            if (response.data.success) {
                setSymbols(response.data.data || []);
            }
        } catch (err) {
            console.error('Failed to fetch symbols:', err);
        } finally {
            setLoadingSymbols(false);
        }
    }, []);

    // Fetch dashboard data
    const fetchDashboard = useCallback(async () => {
        if (!canView) return;
        setLoadingDashboard(true);
        try {
            const response = await axiosInstance.get('/api/market-data-ingestion/liquidity/dashboard');
            if (response.data.success) {
                setDashboardData(response.data.data || response.data);
            } else {
                setError(response.data.message || 'Failed to fetch dashboard');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch liquidity dashboard');
        } finally {
            setLoadingDashboard(false);
        }
    }, [canView]);

    // Fetch correlations table
    const fetchCorrelations = useCallback(async () => {
        if (!canView) return;
        setCorrLoading(true);
        try {
            const params = new URLSearchParams({ page: corrPage + 1, per_page: corrRowsPerPage });
            if (corrSymbolFilter) params.append('symbol_id', corrSymbolFilter.id);

            const response = await axiosInstance.get(`/api/market-data-ingestion/liquidity/correlations?${params.toString()}`);
            if (response.data.success) {
                setCorrelations(response.data.data || []);
                setCorrTotalCount(response.data.pagination?.total || 0);
            } else {
                setError(response.data.message || 'Failed to fetch correlations');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch correlations');
        } finally {
            setCorrLoading(false);
        }
    }, [canView, corrPage, corrRowsPerPage, corrSymbolFilter]);

    useEffect(() => {
        if (canView) {
            fetchSymbols();
            fetchDashboard();
        }
    }, [fetchSymbols, fetchDashboard, canView]);

    useEffect(() => {
        if (canView && activeTab === 1) {
            fetchCorrelations();
        }
    }, [fetchCorrelations, canView, activeTab]);

    // Handlers
    const handleTabChange = (event, newValue) => setActiveTab(newValue);

    const handleCalculateCorrelation = async () => {
        if (!calcSymbol) return;
        setCalculating(true);
        setCalcResult(null);
        setError(null);
        try {
            const payload = {
                symbol_id: calcSymbol.id,
                window_days: calcWindow,
            };
            if (calcRegime) payload.macro_regime = calcRegime;

            const response = await axiosInstance.post('/api/market-data-ingestion/liquidity/calculate-correlation', payload);
            if (response.data.success) {
                setCalcResult(response.data.data || response.data);
                fetchCorrelations();
            } else {
                setError(response.data.message || 'Calculation failed');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to calculate correlation');
        } finally {
            setCalculating(false);
        }
    };

    const handleDetectDivergence = async () => {
        if (!divSymbol) return;
        setDetecting(true);
        setDivResult(null);
        setError(null);
        try {
            const response = await axiosInstance.post(`/api/market-data-ingestion/liquidity/detect-divergence/${divSymbol.id}`);
            if (response.data.success) {
                setDivResult(response.data.data || response.data);
            } else {
                setError(response.data.message || 'Detection failed');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to detect divergence');
        } finally {
            setDetecting(false);
        }
    };

    const handleLoadPredictive = async () => {
        if (!predSymbol) return;
        setLoadingPredictive(true);
        setPredictiveResult(null);
        setError(null);
        try {
            const response = await axiosInstance.get(`/api/market-data-ingestion/liquidity/predictive-model/${predSymbol.id}`);
            if (response.data.success) {
                setPredictiveResult(response.data.data || response.data);
            } else {
                setError(response.data.message || 'Failed to load predictive model');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load predictive model');
        } finally {
            setLoadingPredictive(false);
        }
    };

    const handleCorrChangePage = (event, newPage) => setCorrPage(newPage);
    const handleCorrChangeRowsPerPage = (event) => { setCorrRowsPerPage(parseInt(event.target.value, 10)); setCorrPage(0); };

    // Symbol Autocomplete renderer
    const renderSymbolAutocomplete = (value, onChange, label = 'Select Symbol') => (
        <Autocomplete
            options={symbols}
            getOptionLabel={(option) => `${option.symbol} - ${option.company_name || ''}`}
            value={value}
            onChange={(e, newValue) => onChange(newValue)}
            loading={loadingSymbols}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    size="small"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loadingSymbols ? <CircularProgress size={18} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">You do not have permission to view the Fed Liquidity Engine.</Alert>
            </Box>
        );
    }

    // ==================== RENDER: Dashboard Tab ====================
    const renderDashboard = () => {
        if (loadingDashboard) {
            return (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <CircularProgress />
                    <Typography color="text.secondary" sx={{ mt: 2 }}>Loading liquidity data...</Typography>
                </Box>
            );
        }

        if (!dashboardData) {
            return (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <WaterIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1.5 }} />
                    <Typography variant="h6" color="text.secondary">No liquidity data available</Typography>
                </Box>
            );
        }

        const netLiq = dashboardData.latest_net_liquidity || {};
        const isRising = (netLiq.change_from_previous || 0) >= 0;

        return (
            <Box>
                {/* Net Liquidity Card */}
                <Card sx={{
                    mb: 3,
                    borderRadius: '16px',
                    border: `2px solid ${isRising ? '#10b981' : '#ef4444'}`,
                    boxShadow: 'none',
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <Box sx={{
                                p: 1.5,
                                borderRadius: '14px',
                                bgcolor: isRising ? '#dcfce7' : '#fee2e2',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <WaterIcon sx={{ fontSize: 28, color: isRising ? '#10b981' : '#ef4444' }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontFamily, color: '#64748b', fontWeight: 500, fontSize: '0.85rem' }}>
                                    Fed Net Liquidity
                                </Typography>
                                <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: isRising ? '#10b981' : '#ef4444', lineHeight: 1.2 }}>
                                    {formatBillions(netLiq.net_liquidity)}
                                </Typography>
                            </Box>
                            <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                                    {isRising ? (
                                        <ArrowUpwardIcon sx={{ fontSize: 20, color: '#10b981' }} />
                                    ) : (
                                        <ArrowDownwardIcon sx={{ fontSize: 20, color: '#ef4444' }} />
                                    )}
                                    <Typography sx={{ fontFamily, fontWeight: 700, color: isRising ? '#10b981' : '#ef4444', fontSize: '1.1rem' }}>
                                        {formatBillions(netLiq.change_from_previous)}
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.8rem' }}>
                                    {formatPercent(netLiq.change_percent)}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ bgcolor: '#f8fafc', borderRadius: '12px', p: 2 }}>
                            <Typography sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.8rem', mb: 1 }}>
                                Formula: Fed Balance Sheet - TGA - RRP
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>Fed Balance Sheet</Typography>
                                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
                                        {formatBillions(netLiq.fed_balance_sheet)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>TGA</Typography>
                                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
                                        {formatBillions(netLiq.tga)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>Reverse Repo (RRP)</Typography>
                                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
                                        {formatBillions(netLiq.rrp)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </CardContent>
                </Card>

                {/* Divergence Alerts */}
                {dashboardData.active_divergences && dashboardData.active_divergences.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <WarningIcon sx={{ color: '#f59e0b' }} />
                            Divergence Alerts
                        </Typography>
                        <Grid container spacing={2}>
                            {dashboardData.active_divergences.map((div, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Card sx={{
                                        borderRadius: '16px',
                                        border: '1px solid #fbbf24',
                                        bgcolor: '#fffbeb',
                                        boxShadow: 'none',
                                    }}>
                                        <CardContent sx={{ p: 2.5 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                                    {div.symbol || '-'}
                                                </Typography>
                                                <Chip
                                                    label={DivergenceDirectionLabels[div.divergence_direction] || div.divergence_direction || 'Detected'}
                                                    size="small"
                                                    color={DivergenceDirectionColors[div.divergence_direction] || 'warning'}
                                                    sx={{ fontWeight: 600 }}
                                                />
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>
                                                    Correlation: <strong>{formatCorrelation(div.correlation_strength)}</strong>
                                                </Typography>
                                                {div.macro_regime && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>Regime:</Typography>
                                                        <Chip
                                                            label={MacroRegimeLabels[div.macro_regime] || div.macro_regime}
                                                            size="small"
                                                            color={MacroRegimeColors[div.macro_regime] || 'default'}
                                                            sx={{ fontWeight: 600, height: 22 }}
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}

                {/* Recent Correlations */}
                {dashboardData.latest_correlations && dashboardData.latest_correlations.length > 0 && (
                    <Box>
                        <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AssessmentIcon sx={{ color: ACCENT }} />
                            Recent Correlations
                        </Typography>
                        <Grid container spacing={2}>
                            {dashboardData.latest_correlations.slice(0, 10).map((corr, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Card sx={{
                                        borderRadius: '16px',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: 'none',
                                        transition: 'all 0.2s ease',
                                        '&:hover': { borderColor: ACCENT, boxShadow: `0 4px 20px ${ACCENT}20` },
                                    }}>
                                        <CardContent sx={{ p: 2.5 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
                                                    {corr.symbol || '-'}
                                                </Typography>
                                                <Typography sx={{
                                                    fontFamily,
                                                    fontWeight: 800,
                                                    fontSize: '1.1rem',
                                                    color: parseFloat(corr.correlation_coefficient) >= 0 ? '#10b981' : '#ef4444',
                                                }}>
                                                    {formatCorrelation(corr.correlation_coefficient)}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                                <Chip label={`${corr.window_days || '-'}d`} size="small" variant="outlined" sx={{ height: 22 }} />
                                                {corr.macro_regime && (
                                                    <Chip
                                                        label={MacroRegimeLabels[corr.macro_regime] || corr.macro_regime}
                                                        size="small"
                                                        color={MacroRegimeColors[corr.macro_regime] || 'default'}
                                                        sx={{ height: 22 }}
                                                    />
                                                )}
                                                {corr.strength && (
                                                    <Chip label={corr.strength} size="small" variant="outlined" color="primary" sx={{ height: 22 }} />
                                                )}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
            </Box>
        );
    };

    // ==================== RENDER: Calculate Correlation Tab ====================
    const renderCalculateCorrelation = () => (
        <Box>
            {/* Calculate Form */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 2 }}>
                    Calculate Correlation
                </Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        {renderSymbolAutocomplete(calcSymbol, setCalcSymbol, 'Select Symbol')}
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Window Days</InputLabel>
                            <Select
                                value={calcWindow}
                                label="Window Days"
                                onChange={(e) => setCalcWindow(e.target.value)}
                            >
                                <MenuItem value={30}>30 Days</MenuItem>
                                <MenuItem value={90}>90 Days</MenuItem>
                                <MenuItem value={252}>252 Days</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Macro Regime (Optional)</InputLabel>
                            <Select
                                value={calcRegime}
                                label="Macro Regime (Optional)"
                                onChange={(e) => setCalcRegime(e.target.value)}
                            >
                                <MenuItem value="">None</MenuItem>
                                {Object.entries(MacroRegime).map(([key, value]) => (
                                    <MenuItem key={key} value={value}>{MacroRegimeLabels[value]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Button
                            variant="contained"
                            startIcon={calculating ? <CircularProgress size={18} color="inherit" /> : <CalculateIcon />}
                            onClick={handleCalculateCorrelation}
                            disabled={!calcSymbol || calculating || !canAdd}
                            fullWidth
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, height: 40 }}
                        >
                            Calculate
                        </Button>
                    </Grid>
                </Grid>

                {/* Calculation Result */}
                {calcResult && (
                    <Card sx={{ mt: 3, borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 1.5 }}>
                                Calculation Result
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6} sm={3}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>Correlation Coefficient</Typography>
                                    <Typography sx={{
                                        fontFamily,
                                        fontWeight: 800,
                                        fontSize: '1.2rem',
                                        color: parseFloat(calcResult.correlation_coefficient) >= 0 ? '#10b981' : '#ef4444',
                                    }}>
                                        {formatCorrelation(calcResult.correlation_coefficient)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>Strength</Typography>
                                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                        {calcResult.strength || '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>Divergence</Typography>
                                    <Chip
                                        label={calcResult.has_divergence ? 'Detected' : 'None'}
                                        size="small"
                                        color={calcResult.has_divergence ? 'warning' : 'success'}
                                        sx={{ fontWeight: 600, mt: 0.5 }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>Direction</Typography>
                                    {calcResult.divergence_direction ? (
                                        <Chip
                                            label={DivergenceDirectionLabels[calcResult.divergence_direction] || calcResult.divergence_direction}
                                            size="small"
                                            color={DivergenceDirectionColors[calcResult.divergence_direction] || 'default'}
                                            sx={{ fontWeight: 600, mt: 0.5 }}
                                        />
                                    ) : (
                                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>-</Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                )}
            </Paper>

            {/* Correlations Table */}
            <Paper>
                <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" sx={{ fontFamily, fontWeight: 700 }}>Correlations</Typography>
                        <Chip label={`${corrTotalCount} records`} size="small" />
                    </Box>
                    <Box sx={{ minWidth: 200 }}>
                        {renderSymbolAutocomplete(corrSymbolFilter, (val) => { setCorrSymbolFilter(val); setCorrPage(0); }, 'Filter by Symbol')}
                    </Box>
                </Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Symbol</TableCell>
                                <TableCell align="right">Coefficient</TableCell>
                                <TableCell>Window</TableCell>
                                <TableCell>Regime</TableCell>
                                <TableCell>Divergence</TableCell>
                                <TableCell>Direction</TableCell>
                                <TableCell>Strength</TableCell>
                                <TableCell align="right">Pred 30d</TableCell>
                                <TableCell align="right">Pred 60d</TableCell>
                                <TableCell align="right">Pred 90d</TableCell>
                                <TableCell>Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {corrLoading ? (
                                <TableRow>
                                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                                </TableRow>
                            ) : correlations.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                                        <AssessmentIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                        <Typography color="text.secondary">No correlations calculated yet</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                correlations.map((row, index) => (
                                    <TableRow key={row.id || index} hover>
                                        <TableCell>
                                            <Typography fontWeight="bold" variant="body2">{row.symbol || '-'}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" sx={{
                                                fontWeight: 700,
                                                color: parseFloat(row.correlation_coefficient) >= 0 ? '#10b981' : '#ef4444',
                                            }}>
                                                {formatCorrelation(row.correlation_coefficient)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={`${row.window_days || '-'}d`} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell>
                                            {row.macro_regime ? (
                                                <Chip
                                                    label={MacroRegimeLabels[row.macro_regime] || row.macro_regime}
                                                    size="small"
                                                    color={MacroRegimeColors[row.macro_regime] || 'default'}
                                                />
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={row.has_divergence ? 'Yes' : 'No'}
                                                size="small"
                                                color={row.has_divergence ? 'warning' : 'default'}
                                                sx={{ fontWeight: 600 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {row.divergence_direction && row.divergence_direction !== 'NONE' ? (
                                                <Chip
                                                    label={DivergenceDirectionLabels[row.divergence_direction] || row.divergence_direction}
                                                    size="small"
                                                    color={DivergenceDirectionColors[row.divergence_direction] || 'default'}
                                                    sx={{ fontWeight: 600 }}
                                                />
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{row.strength || '-'}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">{row.predictive_30d != null ? formatPercent(row.predictive_30d) : '-'}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">{row.predictive_60d != null ? formatPercent(row.predictive_60d) : '-'}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">{row.predictive_90d != null ? formatPercent(row.predictive_90d) : '-'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {row.calculated_at ? new Date(row.calculated_at).toLocaleDateString() : row.created_at ? new Date(row.created_at).toLocaleDateString() : '-'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={corrTotalCount}
                    page={corrPage}
                    onPageChange={handleCorrChangePage}
                    rowsPerPage={corrRowsPerPage}
                    onRowsPerPageChange={handleCorrChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
            </Paper>
        </Box>
    );

    // ==================== RENDER: Divergence Detector Tab ====================
    const renderDivergenceDetector = () => (
        <Box>
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 2 }}>
                    Divergence Detector
                </Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        {renderSymbolAutocomplete(divSymbol, setDivSymbol, 'Select Symbol')}
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Button
                            variant="contained"
                            startIcon={detecting ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                            onClick={handleDetectDivergence}
                            disabled={!divSymbol || detecting || !canAdd}
                            fullWidth
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, height: 40 }}
                        >
                            Detect Divergence
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Divergence Result */}
            {divResult && (
                <Card sx={{
                    borderRadius: '16px',
                    border: `2px solid ${divResult.divergence_detected ? '#fbbf24' : '#10b981'}`,
                    boxShadow: 'none',
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <Box sx={{
                                p: 1.5,
                                borderRadius: '14px',
                                bgcolor: divResult.divergence_detected ? '#fffbeb' : '#dcfce7',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                {divResult.divergence_detected ? (
                                    <WarningIcon sx={{ fontSize: 28, color: '#f59e0b' }} />
                                ) : (
                                    <TrendingUpIcon sx={{ fontSize: 28, color: '#10b981' }} />
                                )}
                            </Box>
                            <Box>
                                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem' }}>
                                    {divResult.symbol || divSymbol?.symbol || '-'}
                                </Typography>
                                <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>
                                    Divergence Analysis Result
                                </Typography>
                            </Box>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={6} sm={3}>
                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>Divergence Detected</Typography>
                                <Chip
                                    label={divResult.divergence_detected ? 'Yes' : 'No'}
                                    size="small"
                                    color={divResult.divergence_detected ? 'warning' : 'success'}
                                    sx={{ fontWeight: 600, mt: 0.5 }}
                                />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>Direction</Typography>
                                {divResult.direction ? (
                                    <Chip
                                        label={DivergenceDirectionLabels[divResult.direction] || divResult.direction}
                                        size="small"
                                        color={DivergenceDirectionColors[divResult.direction] || 'default'}
                                        sx={{ fontWeight: 600, mt: 0.5 }}
                                    />
                                ) : (
                                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mt: 0.5 }}>-</Typography>
                                )}
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>Liquidity Change</Typography>
                                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                    {formatPercent(divResult.liquidity_change_percent)}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>Price Change</Typography>
                                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                    {formatPercent(divResult.price_change_percent)}
                                </Typography>
                            </Grid>
                        </Grid>

                        {divResult.period_days && (
                            <Box sx={{ mt: 2, bgcolor: '#f8fafc', borderRadius: '12px', p: 1.5 }}>
                                <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>
                                    Period Analyzed: <strong>{divResult.period_days} days</strong>
                                </Typography>
                            </Box>
                        )}

                        {divResult.warning_message && (
                            <Alert severity="warning" sx={{ mt: 2, borderRadius: '12px' }}>
                                {divResult.warning_message}
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            )}
        </Box>
    );

    // ==================== RENDER: Predictive Model Tab ====================
    const renderPredictiveModel = () => (
        <Box>
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 2 }}>
                    Predictive Model
                </Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        {renderSymbolAutocomplete(predSymbol, setPredSymbol, 'Select Symbol')}
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Button
                            variant="contained"
                            startIcon={loadingPredictive ? <CircularProgress size={18} color="inherit" /> : <AssessmentIcon />}
                            onClick={handleLoadPredictive}
                            disabled={!predSymbol || loadingPredictive}
                            fullWidth
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' }, height: 40 }}
                        >
                            Load Model
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Predictive Result */}
            {predictiveResult && (
                <Box>
                    {/* Latest Correlation Info */}
                    <Card sx={{ mb: 3, borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 2, fontSize: '1rem' }}>
                                Latest Correlation
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6} sm={3}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>Coefficient</Typography>
                                    <Typography sx={{
                                        fontFamily,
                                        fontWeight: 800,
                                        fontSize: '1.2rem',
                                        color: parseFloat(predictiveResult.correlation_coefficient) >= 0 ? '#10b981' : '#ef4444',
                                    }}>
                                        {formatCorrelation(predictiveResult.correlation_coefficient)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>Liquidity Trend</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                        {predictiveResult.liquidity_trend === 'rising' ? (
                                            <>
                                                <ArrowUpwardIcon sx={{ fontSize: 20, color: '#10b981' }} />
                                                <Typography sx={{ fontFamily, fontWeight: 700, color: '#10b981', fontSize: '1rem' }}>
                                                    Rising
                                                </Typography>
                                            </>
                                        ) : (
                                            <>
                                                <ArrowDownwardIcon sx={{ fontSize: 20, color: '#ef4444' }} />
                                                <Typography sx={{ fontFamily, fontWeight: 700, color: '#ef4444', fontSize: '1rem' }}>
                                                    Falling
                                                </Typography>
                                            </>
                                        )}
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>Daily Change Rate</Typography>
                                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                        {formatBillions(predictiveResult.daily_change_rate)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>Window</Typography>
                                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                        {predictiveResult.window_days ? `${predictiveResult.window_days} days` : '-'}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Predictions */}
                    <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUpIcon sx={{ color: ACCENT }} />
                        Forward Estimates
                    </Typography>
                    <Grid container spacing={2}>
                        {[
                            { label: '30-Day Prediction', value: predictiveResult.prediction_30d, key: '30d' },
                            { label: '60-Day Prediction', value: predictiveResult.prediction_60d, key: '60d' },
                            { label: '90-Day Prediction', value: predictiveResult.prediction_90d, key: '90d' },
                        ].map((pred) => (
                            <Grid item xs={12} sm={4} key={pred.key}>
                                <Card sx={{
                                    borderRadius: '16px',
                                    border: '1px solid #e2e8f0',
                                    boxShadow: 'none',
                                    transition: 'all 0.2s ease',
                                    '&:hover': { borderColor: ACCENT, boxShadow: `0 4px 20px ${ACCENT}20`, transform: 'translateY(-2px)' },
                                }}>
                                    <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                                        <Typography sx={{ fontFamily, color: '#64748b', fontWeight: 500, fontSize: '0.85rem', mb: 1 }}>
                                            {pred.label}
                                        </Typography>
                                        <Typography sx={{
                                            fontFamily,
                                            fontWeight: 800,
                                            fontSize: '1.5rem',
                                            color: pred.value != null ? (parseFloat(pred.value) >= 0 ? '#10b981' : '#ef4444') : '#94a3b8',
                                        }}>
                                            {formatBillions(pred.value)}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}
        </Box>
    );

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Gradient Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, right: 80, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />

                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <WaterIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                                Fed Net Liquidity Correlation Engine
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                Historical correlation analysis between Fed Net Liquidity and asset prices
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Tabs */}
                <Paper sx={{ mb: 3, borderRadius: '12px' }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none' },
                        }}
                    >
                        <Tab label="Dashboard" />
                        <Tab label="Calculate Correlation" />
                        <Tab label="Divergence Detector" />
                        <Tab label="Predictive Model" />
                    </Tabs>
                </Paper>

                {/* Tab Content */}
                {activeTab === 0 && renderDashboard()}
                {activeTab === 1 && renderCalculateCorrelation()}
                {activeTab === 2 && renderDivergenceDetector()}
                {activeTab === 3 && renderPredictiveModel()}

                {/* Important Note */}
                <Alert
                    severity="info"
                    icon={<WaterIcon />}
                    sx={{
                        mt: 4,
                        borderRadius: '12px',
                        fontFamily,
                        '& .MuiAlert-message': { fontFamily },
                    }}
                >
                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem' }}>
                        Fed Net Liquidity = Federal Reserve Balance Sheet - TGA - RRP. Rising Net Liquidity generally supports asset prices and compresses vol.
                    </Typography>
                </Alert>
            </Box>
        </Box>
    );
};

export default FedLiquidityEnginePage;
